import React from 'react';
import { NavLink } from 'react-router-dom';

import BackToSearch from '../../common/BackToSearch';
import ProtocolPageHeader from './Protocol/Detail/ProtocolPageHeader';
import CardBase from '../../common/CardBase';
import ProtocolCardBody from './Protocol/Card/ProtocolCardBody';
import ProtocolCardLinks from './Protocol/Card/ProtocolCardLinks';
import FeaturedCard from '../../common/FeaturedCard/FeaturedCard';
import FeaturedCardDynamic from '../../common/FeaturedCard/FeaturedCardDynamic';
import AlertFooter from '../../common/AlertFooter';

import { makeStyles, useTheme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Container from '@material-ui/core/Container';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Box from '@material-ui/core/Box';

import PrintOutlinedIcon from '@material-ui/icons/PrintOutlined';

import Icon from '../../utils/icon';

import SummaryInfo from '../SummaryInfo';
import { useSiteInfo } from '../../crafter_site_core/hooks';
import { urlFromItemPathAndSlug } from '../EnterpriseContentDetailViewSlugSupport';
import { usePencil } from '../../crafter_site_core/IceSupport';

const useStyles = makeStyles((theme) => ({
  heroDisplay: {
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  featuredDisplay: {
    [theme.breakpoints.up('md')]: {
      display: 'none'
    }
  },
  quickinfosmall: {
    paddingBottom: theme.spacing(3),
    fontStyle: 'italic',
    color: theme.palette.primary.main,
    [theme.breakpoints.up('sm')]: {
      display: 'none'
    }
  },
  logocontainer: {
    padding: theme.spacing(2, 0.5),
    '& svg': {
      width: '100%'
    }
  }
}));

export default function (props) {
  const ice = usePencil(props);
  function printPage(e) {
    e.preventDefault();
    window.print();
    return false;
  }
  const classes = useStyles();
  const theme = useTheme();
  const {
    format,
    model: {
      craftercms: { path },
      summary = SummaryInfo(props.model),
      body_html_raw,
      categories_o,
      referenceBlock_html_raw,
      pdf_o,
      slug_s
    }
  } = props;
  const siteinfo = useSiteInfo();
  let cardImgUrl = '';

  if (siteinfo != null) {
    cardImgUrl = siteinfo.protocolDefaultCard_s;
  }

  if (summary.cardImage != null) {
    cardImgUrl = summary.cardImage;
  }

  let sitewideDisclaimer = '';
  if (siteinfo != null) {
    sitewideDisclaimer =
      siteinfo.sitewideDisclaimer_o.item[0].component.body_html;
  }
  switch (format) {
    case 'DETAIL':
      return (
        <>
          <Box width={270} className={classes.logocontainer + ' print-only'}>
            <Icon
              name='metagenics-institute-logo-stacked'
              heightsize={100}
              color='#002A54'
            />
          </Box>
          <Box mb={4}>
            {/* BackToSearch is commented out until search can run with same filters on press or show same results (perhaps global context / state) when navigating back to the search page (memorisation (useMemo) not a use case for this perhaps?) */}
            <BackToSearch />
            <ProtocolPageHeader
              ice={ice}
              contenttype='Protocol'
              categories={categories_o}
              title={summary.title}
              subtitle={summary.subtitle}
              pdf={pdf_o != null && pdf_o[0].craftercms.path}
            />
            <Container maxWidth='lg'>
              <Box
                display='flex'
                alignItems='center'
                justifyContent='space-between'
                className={classes.quickinfosmall + ' no-print'}
                pr={3}
              >
                <Box
                  {...ice}
                  pr={3}
                  display='flex'
                  alignItems='center'
                  justifyContent='center'
                >
                  <Typography
                    variant='overline'
                    style={{ fontWeight: 700, paddingRight: theme.spacing(1) }}
                  >
                    Quick Links:
                  </Typography>
                  <Tooltip title='Print' aria-label='print'>
                    <IconButton onClick={printPage} aria-label='print'>
                      <PrintOutlinedIcon color='secondary' />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              <Box {...ice} width={1} pb={12}>
                <div
                  className='bodyhtmlraw pbody'
                  dangerouslySetInnerHTML={{ __html: body_html_raw }}
                />
              </Box>
              {referenceBlock_html_raw && (
                <Box pb={12}>
                  <Typography variant='h4' gutterBottom>
                    References
                  </Typography>
                  <div
                    className='bodyhtmlraw'
                    dangerouslySetInnerHTML={{
                      __html: referenceBlock_html_raw
                    }}
                  />
                </Box>
              )}
            </Container>
            <Box
              position='sticky'
              left={0}
              bottom={0}
              right={0}
              height={56}
              mt={12}
            >
              <AlertFooter info={sitewideDisclaimer} />
            </Box>
          </Box>
        </>
      );
    case 'CARD':
      return (
        <>
          <CardBase contenttype='Protocol' categories={categories_o}>
            <NavLink to={urlFromItemPathAndSlug(path, slug_s)} exact>
              <ProtocolCardBody
                title={summary.title}
                subtitle={summary.subtitle}
                snippet={summary.snippet}
              />
            </NavLink>
            <ProtocolCardLinks
              buttontext='read now'
              link={urlFromItemPathAndSlug(path, slug_s)}
            />
          </CardBase>
        </>
      );
    case 'FEATURED':
      return (
        <FeaturedCardDynamic
          contenttype='Protocol'
          title={summary.title}
          subtitle={summary.subtitle}
          snippet={summary.snippet}
          buttontext='read now'
          categories={categories_o}
          image={cardImgUrl}
          icon='protocol'
          link={urlFromItemPathAndSlug(path, slug_s)}
        />
      );
    case 'HERO':
      return (
        <Container maxWidth='lg'>
          <div className={classes.heroDisplay}>
            <FeaturedCard
              contenttype='Protocol'
              title={summary.title}
              subtitle={summary.subtitle}
              snippet={summary.snippet}
              buttontext='read now'
              categories={categories_o}
              image={cardImgUrl}
              link={urlFromItemPathAndSlug(path, slug_s)}
              icon='protocol'
            />
          </div>
          <div className={classes.featuredDisplay}>
            <FeaturedCardDynamic
              contenttype='Protocol'
              title={summary.title}
              subtitle={summary.subtitle}
              snippet={summary.snippet}
              buttontext='read now'
              categories={categories_o}
              image={cardImgUrl}
              icon='protocol'
              link={urlFromItemPathAndSlug(path, slug_s)}
            />
          </div>
        </Container>
      );
    default:
      console.error(`Unknown format "${format}"."`);
      return <h1>Unknown format.</h1>;
  }
}
